import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// material-ui
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Typography from '@mui/material/Typography';

// project imports
import IconButton from 'components/@extended/IconButton';
import MainCard from 'components/MainCard';
import Transitions from 'components/@extended/Transitions';
import useAuth from 'contexts/AuthContext';
import { apiFetch } from 'api/client';

// assets
import BellOutlined from '@ant-design/icons/BellOutlined';

// ==============================|| HEADER CONTENT - NOTIFICATION ||============================== //
// Real, actionable items only. The template shipped hardcoded sample
// notifications about people who don't exist; this surfaces things that
// would otherwise be found late — undelivered invites, or a published
// course with an empty lesson.

export default function Notification() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [alerts, setAlerts] = useState([]);

  const isAdmin = user?.role === 'admin';

  const load = useCallback(() => {
    if (!isAdmin) return;
    apiFetch('/admin/alerts/')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => data && setAlerts(data.alerts))
      .catch(() => {
        /* a failed check shouldn't disrupt the header */
      });
  }, [isAdmin]);

  useEffect(load, [load]);

  // Students have nothing actionable here, so they don't get a bell at all
  // rather than an empty one.
  if (!isAdmin) return null;

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) return;
    setOpen(false);
  };

  const go = (link) => {
    setOpen(false);
    navigate(link);
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 0.75 }}>
      <IconButton
        color="secondary"
        sx={{ color: 'text.primary', bgcolor: open ? 'grey.100' : 'transparent' }}
        aria-label="open notifications"
        ref={anchorRef}
        aria-haspopup="true"
        onClick={() => {
          load();
          setOpen((prev) => !prev);
        }}
      >
        <Badge badgeContent={alerts.length} color="error">
          <BellOutlined />
        </Badge>
      </IconButton>

      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        transition
        disablePortal
        popperOptions={{ modifiers: [{ name: 'offset', options: { offset: [0, 9] } }] }}
      >
        {({ TransitionProps }) => (
          <Transitions type="grow" position="top-right" in={open} {...TransitionProps}>
            <Paper sx={(theme) => ({ boxShadow: theme.vars.customShadows.z1, width: 360, maxWidth: { xs: 300, md: 360 } })}>
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard title="Needs attention" elevation={0} border={false} content={false}>
                  {alerts.length === 0 ? (
                    <Typography variant="body2" sx={{ color: 'text.secondary', px: 2.5, py: 2.5 }}>
                      Nothing needs your attention.
                    </Typography>
                  ) : (
                    <List sx={{ p: 0 }}>
                      {alerts.map((alert, index) => (
                        <ListItemButton
                          key={index}
                          onClick={() => go(alert.link)}
                          sx={{ borderTop: index ? 1 : 0, borderColor: 'divider', py: 1.5 }}
                        >
                          <ListItemText
                            primary={
                              <Typography variant="subtitle2" sx={{ color: alert.severity === 'error' ? 'error.main' : 'warning.dark' }}>
                                {alert.title}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {alert.detail}
                              </Typography>
                            }
                          />
                        </ListItemButton>
                      ))}
                    </List>
                  )}
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
}
